import { Capacitor } from '@capacitor/core';
import { EventEmitter } from './event-emitter';
import { 
    AdMob, 
    BannerAdOptions, 
    BannerAdSize, 
    BannerAdPosition, 
    BannerAdPluginEvents, 
    AdMobBannerSize, 
    AdOptions, 
    AdLoadInfo, 
    InterstitialAdPluginEvents 
} from '@capacitor-community/admob';
import { delay } from './util';

/**
 * Wire up better events for the ads. The admob plugin by default has no way to unsub from
 * events, which is not convenient.
 */
const adEvents = new EventEmitter();
AdMob.addListener(BannerAdPluginEvents.AdImpression, () => adEvents.emit('bannerAdImpression'));
AdMob.addListener(BannerAdPluginEvents.Loaded, () => adEvents.emit('bannerAdLoaded'))
AdMob.addListener(BannerAdPluginEvents.FailedToLoad, (info) => adEvents.emit('bannerAdFailedToLoad', info));
AdMob.addListener(BannerAdPluginEvents.SizeChanged, (size) => adEvents.emit('bannerSizeChange', size));
AdMob.addListener(BannerAdPluginEvents.Closed, () => adEvents.emit('bannerAdClosed'));
AdMob.addListener(BannerAdPluginEvents.Opened, () => adEvents.emit('bannerAdOpened'));
AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, () => adEvents.emit('interstitialAdFailedToLoad'));
AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => adEvents.emit('interstitialAdDismissed'));
AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => adEvents.emit('interstitialAdFailedToShow'));
AdMob.addListener(InterstitialAdPluginEvents.Loaded, () => adEvents.emit('interstitialAdLoaded'));
AdMob.addListener(InterstitialAdPluginEvents.Showed, () => adEvents.emit('interstitialAdShowed'));

/**
 * Manage banner and get banner size
 */
let bannerSize = {
    width: 50,
    height: 50
};

let bannerLoaded = false;
const getBannerSize = () => bannerSize;
const hideBanner = () => bannerLoaded ? AdMob.hideBanner() : null;
const resumeBanner = () => bannerLoaded ? AdMob.resumeBanner() : null;
const showBanner = async (timeout: number = 3000): Promise<boolean> => {
    let loadPromise = new Promise<boolean>(async (resolve) => {
        if (Capacitor.getPlatform() === 'web'){
            console.log(`Banner Ad not loaded because platform is "${Capacitor.getPlatform()}"`);
            bannerLoaded = false;
            resolve(false);
        }
    
        if (bannerLoaded){
            AdMob.resumeBanner();
            resolve(true);
        }

        const resolvePromise = (loaded: boolean) => {
            adEvents.removeListener(BannerAdPluginEvents.FailedToLoad, handleLoadFailed);
            adEvents.removeListener(BannerAdPluginEvents.Loaded, handleLoaded);
            resolve(loaded);
        }

        const handleLoadFailed = () => {
            bannerLoaded = false;
            resolvePromise(false);
        }

        const handleLoaded = () => {
            bannerLoaded = true;
            resolvePromise(true);
        }
        
        adEvents.on(BannerAdPluginEvents.FailedToLoad, handleLoadFailed);
        adEvents.on(BannerAdPluginEvents.Loaded, handleLoaded);

        AdMob.showBanner({
            margin: 0,
            adId: 'ca-app-pub-6032836321331778/1793759288',
            adSize: BannerAdSize.BANNER,
            position: BannerAdPosition.BOTTOM_CENTER,
            isTesting: process.env.NODE_ENV === 'development'
        })
    })

    return Promise.race<boolean>([loadPromise, delay(timeout, false)]);
}

let interstitialLoaded = false;
const prepareInterstitial = () : Promise<boolean> => {
    return new Promise((resolve) => {
        if (Capacitor.getPlatform() === 'web'){
            interstitialLoaded = false;
            resolve(false);
        }

        const resolvePromise = (loaded: boolean) => {
            adEvents.removeListener(InterstitialAdPluginEvents.FailedToLoad, handleLoadFailed);
            adEvents.removeListener(InterstitialAdPluginEvents.Loaded, handleLoaded);
            resolve(loaded);
        }

        const handleLoadFailed = () => {
            interstitialLoaded = false;
            resolvePromise(false);
        }

        const handleLoaded = () => {
            interstitialLoaded = true;
            resolvePromise(true);
        }
        
        adEvents.on(InterstitialAdPluginEvents.FailedToLoad, handleLoadFailed);
        adEvents.on(InterstitialAdPluginEvents.Loaded, handleLoaded);

        AdMob.prepareInterstitial({
            adId: 'ca-app-pub-6032836321331778/5625193080',
            isTesting: process.env.NODE_ENV === 'development'
        });
    });
}

const showInterstitial = () => {
    return new Promise((resolve) => {
        /**
         * Ad was never loaded or failed to load. Do nothing and just resolve this promise.
         */
        if (!interstitialLoaded){
            resolve(null);
        }

        const handleDismiss = () => {
            adEvents.removeListener(InterstitialAdPluginEvents.Dismissed, handleDismiss);
            adEvents.removeListener(InterstitialAdPluginEvents.FailedToShow, handleDismiss);
            resolve(null);
        }

        adEvents.on(InterstitialAdPluginEvents.Dismissed, handleDismiss);
        adEvents.on(InterstitialAdPluginEvents.FailedToShow, handleDismiss);
        AdMob.showInterstitial();
    });
}

export {
    showBanner,
    hideBanner,
    resumeBanner,
    getBannerSize,
    prepareInterstitial,
    showInterstitial,
    adEvents
}